// Our own Table of Contents extension (the official one is not MIT-licensed).
// An atom block that live-renders the document's h2/h3 outline in the editor.
// It serializes to <div data-type="toc"> — the public help site (Phase 2)
// replaces that marker server-side with a real anchor list.

import { Node, mergeAttributes, type Editor } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import { ListTree } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { NodeDeleteButton } from './NodeDeleteButton'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tableOfContents: {
      insertTableOfContents: () => ReturnType
    }
  }
}

interface HeadingItem {
  level: number
  text: string
}

function collectHeadings(editor: Editor): HeadingItem[] {
  const items: HeadingItem[] = []
  editor.state.doc.descendants(node => {
    if (node.type.name === 'heading' && [ 2, 3 ].includes(node.attrs.level) && node.textContent.trim()) {
      items.push({ level: node.attrs.level, text: node.textContent })
    }
    return true
  })
  return items
}

function TocView({ editor, selected, deleteNode }: NodeViewProps) {
  const [headings, setHeadings] = useState<HeadingItem[]>(() => collectHeadings(editor))

  useEffect(() => {
    const update = () => setHeadings(collectHeadings(editor))
    editor.on('update', update)
    return () => { editor.off('update', update) }
  }, [editor])

  return (
    <NodeViewWrapper
      data-type="toc"
      className={cn(
        'group relative my-3 rounded-md border border-border bg-muted/30 px-4 py-3',
        selected && 'ring-2 ring-primary/70',
      )}
      contentEditable={false}
    >
      <NodeDeleteButton onDelete={deleteNode} label="Remove table of contents" />
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <ListTree className="size-3.5" /> On this page
      </div>
      {headings.length === 0 ? (
        <p className="text-sm text-muted-foreground">Add headings to populate the table of contents.</p>
      ) : (
        <ul className="space-y-0.5 text-sm">
          {headings.map((heading, index) => (
            <li key={index} className={cn('text-primary', heading.level === 3 && 'pl-4')}>
              {heading.text}
            </li>
          ))}
        </ul>
      )}
    </NodeViewWrapper>
  )
}

export const TableOfContents = Node.create({
  name: 'tableOfContents',
  group: 'block',
  atom: true,

  parseHTML() {
    return [ { tag: 'div[data-type="toc"]' } ]
  },

  renderHTML({ HTMLAttributes }) {
    return [ 'div', mergeAttributes(HTMLAttributes, { 'data-type': 'toc', class: 'toc' }) ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(TocView)
  },

  addCommands() {
    return {
      insertTableOfContents:
        () =>
        ({ commands }) =>
          commands.insertContent({ type: this.name }),
    }
  },
})
